/**
 * UpcomingDueBanner — dismissible-per-session amber banner listing due
 * dates (payroll timecard deadlines, personal reminders, admin-configured
 * Deadline entries) that fall inside their own reminder window.
 * Client-side only: computed from data the caller already fetched, no
 * backend job involved (separate from the real Deadline notification
 * delivery in apps.attendance.tasks.send_deadline_notifications — this is
 * just what renders on-screen while the dashboard happens to be open).
 *
 * Visual language copied from SampleDataBanner.js on purpose — same amber
 * border + AlertTriangle icon, so this reads as the same product signal
 * rather than inventing new styling. The live countdown (when an item
 * opts in via showCountdown) gets its own clearly separated pill so it
 * reads as "live/ticking" data distinct from the static due-date text.
 *
 * Usage:
 *   <UpcomingDueBanner items={[
 *     { key: 'payroll-10', label: 'Payroll Timecards Due (Pay Period 10)', dueAt: '2026-06-30T17:00:00', hoursBefore: 24 },
 *     { key: 'reminder-abc', label: 'Finish cert renewal', dueAt: '2026-07-16T17:00:00', hoursBefore: 24 },
 *     // Deadline entries: bannerText overrides label, showCountdown renders a live <Countdown> badge.
 *     { key: 'deadline-xyz', label: 'Board Meeting Report', bannerText: 'Board Meeting report deadline is due Fri 14th', showCountdown: true, dueAt: '2026-07-17T00:00:00', hoursBefore: 24 },
 *   ]} />
 */
import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import Countdown from './Countdown';

export function isDueSoon(dueAt, hoursBefore, now = new Date()) {
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return false;
  const msUntilDue = due.getTime() - now.getTime();
  if (msUntilDue < 0) return false; // already past due — not this banner's job to nag about overdue items
  return msUntilDue <= hoursBefore * 60 * 60 * 1000;
}

function CountdownBadge({ dueAt }) {
  return (
    <span className="inline-flex items-center gap-1 shrink-0 text-xs font-semibold text-amber-800 bg-white border border-amber-300 rounded-full px-2 py-0.5 shadow-sm">
      <Clock className="h-3 w-3" />
      <Countdown targetDate={dueAt} />
    </span>
  );
}

function formatDueAt(dueAt) {
  return new Date(dueAt).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function UpcomingDueBanner({ items = [], testId = 'upcoming-due-banner' }) {
  const now = new Date();
  const dueSoon = items.filter((item) => isDueSoon(item.dueAt, item.hoursBefore ?? 24, now));

  if (dueSoon.length === 0) return null;

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg mb-4"
      data-testid={testId}
    >
      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 text-sm text-amber-700 font-medium">
        {dueSoon.length === 1 ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span>
              {dueSoon[0].bannerText ? (
                <strong>{dueSoon[0].bannerText}</strong>
              ) : (
                <>Due soon: <strong>{dueSoon[0].label}</strong></>
              )}
              {' — '}{formatDueAt(dueSoon[0].dueAt)}
            </span>
            {dueSoon[0].showCountdown && <CountdownBadge dueAt={dueSoon[0].dueAt} />}
            {dueSoon[0].onEmailOptOut && (
              <button
                type="button"
                className="text-xs font-semibold text-amber-900 underline"
                onClick={dueSoon[0].onEmailOptOut}
                data-testid="approver-reminder-email-opt-out"
              >
                {dueSoon[0].emailOptOut?.optedOut ? 'Turn timesheet deadline emails back on' : "Don't email me these"}
              </button>
            )}
          </div>
        ) : (
          <div>
            <p className="mb-1.5">You have {dueSoon.length} things due soon:</p>
            <ul className="space-y-1">
              {dueSoon.map((item) => (
                <li key={item.key} className="flex items-center justify-between gap-3 flex-wrap">
                  <span>{item.bannerText || item.label} — {formatDueAt(item.dueAt)}</span>
                  {item.showCountdown && <CountdownBadge dueAt={item.dueAt} />}
                  {item.onEmailOptOut && (
                    <button
                      type="button"
                      className="text-xs font-semibold text-amber-900 underline"
                      onClick={item.onEmailOptOut}
                      data-testid="approver-reminder-email-opt-out"
                    >
                      {item.emailOptOut?.optedOut ? 'Turn timesheet deadline emails back on' : "Don't email me these"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
